import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

type Theme = 'light' | 'dark';

interface AdminThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

const AdminThemeContext = createContext<AdminThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  isDark: false,
});

export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within an AdminThemeProvider');
  }
  return context;
};

interface AdminThemeProviderProps {
  children: React.ReactNode;
}

export const AdminThemeProvider: React.FC<AdminThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('admin-theme') as Theme;
    return savedTheme || 'light';
  });

  const isDark = theme === 'dark';

  const toggleTheme = () => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      console.log('Toggling theme from', prevTheme, 'to', newTheme);
      localStorage.setItem('admin-theme', newTheme);
      return newTheme;
    });
  };

  useEffect(() => {
    // Apply theme to document body for admin pages
    if (isDark) {
      document.body.classList.add('admin-dark');
      document.body.classList.remove('admin-light');
    } else {
      document.body.classList.add('admin-light');
      document.body.classList.remove('admin-dark');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('admin-dark', 'admin-light');
    };
  }, [isDark]);

  const value: AdminThemeContextType = useMemo(() => ({
    theme,
    toggleTheme,
    isDark,
  }), [theme, isDark]);

  return (
    <AdminThemeContext.Provider value={value}>
      {children}
    </AdminThemeContext.Provider>
  );
};
